import React from 'react';

const TIMING_SLOTS = [
  { key: 'morning', label: 'Morning' },
  { key: 'noon', label: 'Noon' },
  { key: 'evening', label: 'Evening' },
  { key: 'night', label: 'Night' }
];

const defaultTiming = () => ({
  morning: false,
  noon: false,
  evening: false,
  night: false
});

const formatTimingLabel = timing => {
  if (!timing) return '';
  const parts = TIMING_SLOTS.filter(slot => timing[slot.key]).map(slot => slot.label);
  return parts.join(', ');
};

const MedicationItem = ({ item, index, onUpdate, onRemove, canRemove, medicines, loadingMedicines }) => {
  const normalizedTiming = { ...defaultTiming(), ...(item.timing || {}) };

  const handleTimingToggle = slot => {
    const nextTiming = {
      ...normalizedTiming,
      [slot]: !normalizedTiming[slot]
    };
    onUpdate(index, 'timing', nextTiming);
    const timingLabel = formatTimingLabel(nextTiming);
    onUpdate(index, 'frequency', timingLabel || item.frequency || '');
  };

  return (
    <div className="border rounded-lg p-4 mb-4 bg-gray-50" style={{fontFamily:'sans-serif'}}>
      <div className="flex justify-between items-center mb-3">
        <h5 className="text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Medication {index + 1}</h5>
        {canRemove && (
          <button
            onClick={() => onRemove(index)}
            className="text-red-600 hover:text-red-800 text-sm"
          >
            Remove
          </button>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Medicine *</label>
          <select 
            className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-md font-semibold text-gray-600 uppercase tracking-wide focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all"
            value={item.medicine || ''}
            onChange={(e) => {
              const selectedMedicine = medicines.find(med => med._id === e.target.value);
              onUpdate(index, 'medicine', e.target.value);
              
              if (selectedMedicine) {
                // Auto-fill fields based on master data
                onUpdate(index, 'name', selectedMedicine.name);
                onUpdate(index, 'days', selectedMedicine.days || 5);
                onUpdate(index, 'instruction', selectedMedicine.instruction || '');
                
                // Generate dose from mgPerUnit and doseQuantity
                const dose = selectedMedicine.mgPerUnit 
                  ? `${selectedMedicine.mgPerUnit}mg x ${selectedMedicine.doseQuantity || 1}`
                  : `${selectedMedicine.doseQuantity || 1} unit`;
                onUpdate(index, 'dose', dose);
                
                // Generate frequency from timing and dosesPerDay
                let frequency = '';
                if (selectedMedicine.timing) {
                  const timingSnapshot = {
                    morning: Boolean(selectedMedicine.timing.morning),
                    noon: Boolean(selectedMedicine.timing.noon),
                    evening: Boolean(selectedMedicine.timing.evening),
                    night: Boolean(selectedMedicine.timing.night)
                  };
                  onUpdate(index, 'timing', timingSnapshot);
                  frequency = formatTimingLabel(timingSnapshot);
                } else if (selectedMedicine.dosesPerDay) {
                  frequency = `${selectedMedicine.dosesPerDay} time${selectedMedicine.dosesPerDay > 1 ? 's' : ''} daily`;
                }
                onUpdate(index, 'frequency', frequency);
                
                // Set route (default to Oral if not specified)
                onUpdate(index, 'route', selectedMedicine.route || 'oral');
              }
            }}
            disabled={loadingMedicines}
          >
            <option value="" className="text-md font-semibold text-gray-600 uppercase tracking-wide">Select Medicine</option>
            {medicines.map((medicine) => (
              <option key={medicine._id} value={medicine._id} className="text-md font-semibold text-gray-600 uppercase tracking-wide">
                {medicine.name} - {medicine.drug?.name || medicine.drug} 
                {medicine.manufacturer?.name && ` (${medicine.manufacturer.name})`}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Dose</label>
          <input 
            type="text" 
            className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-md font-semibold text-gray-600 uppercase tracking-wide focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all"
            value={item.dose}
            onChange={(e) => onUpdate(index, 'dose', e.target.value)}
            placeholder="e.g., 500mg"
          />
        </div>
        <div className="md:col-span-3 mt-8 mb-8">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span className="text-md font-semibold text-gray-600 uppercase tracking-wide">Frequency:</span>
            {TIMING_SLOTS.map(slot => (
              <label key={slot.key} className="inline-flex items-center gap-2 text-md">
                <input
                  type="checkbox"
                  className="rounded text-[#2D869C] focus:ring-[#9DC9D4] border-gray-300"
                  checked={normalizedTiming[slot.key]}
                  onChange={() => handleTimingToggle(slot.key)}
                />
                <span className="text-md font-semibold text-gray-600 uppercase tracking-wide">{slot.label}</span>
              </label>
            ))}
          </div>
          <input 
            type="text" 
            className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-md font-semibold text-gray-600 uppercase tracking-wide focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all"
            value={item.frequency}
            onChange={(e) => onUpdate(index, 'frequency', e.target.value)}
            placeholder="e.g., Morning, Noon, Night"
          />
        </div>
        <div>
          <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Route</label>
          <input 
            type="text" 
            className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-md font-semibold text-gray-600 uppercase tracking-wide focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all"
            value={item.route}
            onChange={(e) => onUpdate(index, 'route', e.target.value)}
            placeholder="e.g., Oral"
          />
        </div>
        <div>
          <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Duration (Days)</label>
          <input 
            type="number" 
            className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-md font-semibold text-gray-600 uppercase tracking-wide focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all"
            value={item.days}
            onChange={(e) => onUpdate(index, 'days', e.target.value)}
            placeholder="7"
            min="1"
            max="365"
          />
        </div>
        <div>
          <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Instructions</label>
          <input 
            type="text" 
            className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-md font-semibold text-gray-600 uppercase tracking-wide focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all"
            value={item.instruction}
            onChange={(e) => onUpdate(index, 'instruction', e.target.value)}
            placeholder="e.g., Take after meals"
          />
        </div>
      </div>
    </div>
  );
};

export default MedicationItem;
